import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, of, Observable, BehaviorSubject } from 'rxjs';
import { ProfileData, User, UserDto, Users } from '../models/users';
import {
  ActionsModule,
  PermissionByUser,
  Permissions,
} from '../models/permission';

const generateEndpoint = environment.generateEndpoint;

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private userChangedSubject = new BehaviorSubject<string | null>(null);
  permissions: ActionsModule[] = [];

  constructor(private http: HttpClient) {}

  getUsers(query: string) {
    return this.http.get<Users>(generateEndpoint(['users' + query])).pipe(
      catchError((err) => {
        return of(undefined);
      })
    );
  }

  getUser(userId: string) {
    return this.http
      .get<{ profile: User }>(generateEndpoint(['users', userId, 'profile']))
      .pipe(
        catchError((err) => {
          return of(undefined);
        })
      );
  }

  getPermissions(userId: number) {
    return this.http
      .get<Permissions>(generateEndpoint(['users', 'permissions-list', userId]))
      .pipe(
        catchError((err) => {
          return of(undefined);
        })
      );
  }

  getActionsByModule(userId: number, moduleId: number) {
    return this.http
      .get<ActionsModule>(
        generateEndpoint(['users', userId, 'module', moduleId, 'actions'])
      )
      .pipe(
        catchError((err) => {
          return of(undefined);
        })
      );
  }

  saveUser(user: UserDto) {
    return this.http.post<UserDto>(generateEndpoint(['users']), user);
  }

  updateUser(user: UserDto, userId: string) {
    return this.http.put<UserDto>(generateEndpoint(['users', userId]), user);
  }

  getPermissionsByUser(userId: number) {
    return this.http.get<PermissionByUser[]>(
      generateEndpoint(['accounts', 'permissions', userId])
    );
  }

  getProfileData(userId: number) {
    return this.http.get<ProfileData>(
      generateEndpoint(['users', userId, 'profile'])
    );
  }

  updatePwd(
    userId: number,
    passwords: { currentPassword: string; newPassword: string }
  ) {
    return this.http.post<any>(
      generateEndpoint(['accounts', 'change_password', userId]),
      passwords
    );
  }

  deleteUser(users: number[], code: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        userIds: users,
        code: code,
      },
    };

    return this.http.delete<any>(generateEndpoint(['users']), options);
  }

  updloadFileProgress(
    file: File,
    fieldFile: string,
    userId: number
  ): Observable<HttpEvent<any>> {
    const formdata: FormData = new FormData();

    formdata.append(fieldFile, file);
    formdata.append('delete', '0');

    return this.http
      .post<any>(
        generateEndpoint(['accounts', 'image_profile', userId]),
        formdata,
        {
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        catchError((err) => {
          return of(err);
        })
      );
  }

  removeFile(userId: number) {
    const formdata: FormData = new FormData();
    formdata.append('file', 'null');
    formdata.append('delete', '1');
    return this.http
      .post<any>(
        generateEndpoint(['accounts', 'image_profile', userId]),
        formdata,
        {
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        catchError((err) => {
          return of(err);
        })
      );
  }

  /** Observable que emite cuando hay un cambio en el usuario */
  onUserChanged(): Observable<string | null> {
    return this.userChangedSubject.asObservable();
  }

  /** Método para indicar que el usuario ha cambiado */
  notifyUserChange(value: string | null): void {
    this.userChangedSubject.next(value);
  }

  generateOtpCode() {
    return this.http.post<any>(
      generateEndpoint(['accounts', 'authorizations']),
      { type: 'otp', action: 'delete_user_request' }
    );
  }
}
